import React, { PropsWithChildren } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@economic/taco';
import { useLocalStorage } from '@economic/react-app';
import { LoadingBox } from '~/shared-ui';
import { Column, Row } from '../../../../../shared/components/Grid/Grid';
import { Field } from '../../../../../shared/components/Form';
import { t, useLocalization } from '../../../../../shared/components/l10n/l10n';
import { useEmailOptionsPdfSelected, useEmailOptionsOioSelected } from '../../invoices/utils';

export interface SendEmailOptionsDrawerProps {
    hideRemember?: boolean;
    hideRecipientsSection?: boolean;
}

export function SendEmailOptionsDrawer({
    hideRemember,
    hideRecipientsSection,
    children,
}: PropsWithChildren<SendEmailOptionsDrawerProps>) {
    const location = useLocation();

    const [additionalRecipientsVisible, setAdditionalRecipientsVisible] = useLocalStorage<boolean>(
        `${location.pathname}.additionalRecipientsVisible`,
        true
    );

    const textIds = [
        'FORM_BOOKANDSEND_EMAIL_OPTIONS_RECIPIENTS',
        'FORM_BOOKANDSEND_EMAIL_OPTIONS_TOGGLE_CC_BCC',
        'FORM_BOOKANDSEND_EMAIL_OPTIONS_CC',
        'FORM_BOOKANDSEND_EMAIL_OPTIONS_BCC',
        'FORM_BOOKANDSEND_EMAIL_OPTIONS_SUBJECT',
        'FORM_BOOKANDSEND_EMAIL_OPTIONS_REMEMBER',
        'FORM_BOOKANDSEND_EMAIL_OPTIONS_BODY',
        'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_CLOSE',
        'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OPEN',
        'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OIOXML_ATTACHMENT',
        'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_ATTACHMENT',
        'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_ENCRYPT',
        'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OIOXML_LINK',
        'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_LINK',
    ];
    const loadingTexts = useLocalization(textIds);

    if (loadingTexts) {
        return <LoadingBox />;
    }

    return (
        <div className="book-and-send-dialog__email-options">
            <Row>
                <Column>
                    {!hideRecipientsSection && (
                        <Field.Text
                            id="recepients"
                            label={t`FORM_BOOKANDSEND_EMAIL_OPTIONS_RECIPIENTS`}
                            name="emailOptions.recipients"
                            supplementary={
                                <Button
                                    className="book-and-send-dialog__email-options__toggle"
                                    appearance="discrete"
                                    onClick={() => setAdditionalRecipientsVisible(!additionalRecipientsVisible)}>
                                    {t`FORM_BOOKANDSEND_EMAIL_OPTIONS_TOGGLE_CC_BCC`}
                                </Button>
                            }
                        />
                    )}
                    {!hideRecipientsSection && additionalRecipientsVisible && (
                        <>
                            <Field.Text
                                id="recipientsCc"
                                label={t`FORM_BOOKANDSEND_EMAIL_OPTIONS_CC`}
                                name="emailOptions.recipientsCc"
                            />
                            <Field.Text
                                id="recipientsBcc"
                                label={t`FORM_BOOKANDSEND_EMAIL_OPTIONS_BCC`}
                                name="emailOptions.recipientsBcc"
                            />
                        </>
                    )}

                    <Field.Text
                        id="subject"
                        label={t`FORM_BOOKANDSEND_EMAIL_OPTIONS_SUBJECT`}
                        name="emailOptions.subject"
                        supplementary={
                            hideRemember !== true && (
                                <Field.Checkbox
                                    id="saveSubject"
                                    text={t`FORM_BOOKANDSEND_EMAIL_OPTIONS_REMEMBER`}
                                    name="emailOptions.saveSubject"
                                    hideLabel
                                />
                            )
                        }
                    />
                    <Field.TextArea
                        id="body"
                        label={t`FORM_BOOKANDSEND_EMAIL_OPTIONS_BODY`}
                        name="emailOptions.body"
                        className="email-body"
                        supplementary={
                            hideRemember !== true && (
                                <Field.Checkbox
                                    id="saveBody"
                                    text={t`FORM_BOOKANDSEND_EMAIL_OPTIONS_REMEMBER`}
                                    name="emailOptions.saveBody"
                                    hideLabel
                                />
                            )
                        }
                    />
                </Column>
            </Row>
            {children}
        </div>
    );
}

export const FullAttachmentOptions = () => {
    const location = useLocation();
    const isPdfSelected = useEmailOptionsPdfSelected();
    const isOioSelected = useEmailOptionsOioSelected();
    const [advancedOptionsVisible, setAdvancedOptionsVisible] = useLocalStorage<boolean>(
        `${location.pathname}.advancedOptionsVisible`,
        !isPdfSelected && !isOioSelected
    );

    return (
        <>
            <Row>
                <Column>
                    <Button
                        className="book-and-send-dialog__toggle-options"
                        appearance="discrete"
                        onClick={() => setAdvancedOptionsVisible(!advancedOptionsVisible)}>
                        {advancedOptionsVisible
                            ? t`FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_CLOSE`
                            : t`FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OPEN`}
                    </Button>
                </Column>
            </Row>
            {advancedOptionsVisible && (
                <Row>
                    <Column>
                        <Field.Checkbox
                            fieldClassName="field--no-height"
                            name="emailOptions.includeOioAsAttachment"
                            text={t`FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OIOXML_ATTACHMENT`}
                        />
                        <Field.Checkbox
                            fieldClassName="field--no-height"
                            name="emailOptions.includePdfAsAttachment"
                            text={t`FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_ATTACHMENT`}
                        />
                        <Field.Checkbox
                            fieldClassName="field--no-height"
                            name="emailOptions.encrypt"
                            text={t`FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_ENCRYPT`}
                        />
                    </Column>
                    <Column>
                        <Field.Checkbox
                            fieldClassName="field--no-height"
                            name="emailOptions.includeOioLink"
                            text={t`FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OIOXML_LINK`}
                        />
                        <Field.Checkbox
                            fieldClassName="field--no-height"
                            name="emailOptions.includePdfLink"
                            text={t`FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_LINK`}
                        />
                    </Column>
                </Row>
            )}
        </>
    );
};

export const PdfAttachmentOptions = () => {
    const location = useLocation();
    const [advancedOptionsVisible, setAdvancedOptionsVisible] = useLocalStorage<boolean>(
        `${location.pathname}.advancedOptionsVisible`,
        false
    );

    return (
        <>
            <Row>
                <Column>
                    <Button appearance="discrete" onClick={() => setAdvancedOptionsVisible(!advancedOptionsVisible)}>
                        {advancedOptionsVisible
                            ? t`FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_CLOSE`
                            : t`FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OPEN`}
                    </Button>
                </Column>
            </Row>
            {advancedOptionsVisible && (
                <Row>
                    <Column>
                        <Field.Checkbox
                            fieldClassName="field--no-height"
                            name="emailOptions.includePdfAsAttachment"
                            text={t`FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_ATTACHMENT`}
                        />
                        <Field.Checkbox
                            fieldClassName="field--no-height"
                            name="emailOptions.encrypt"
                            text={t`FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_ENCRYPT`}
                        />
                    </Column>
                    <Column>
                        <Field.Checkbox
                            fieldClassName="field--no-height"
                            name="emailOptions.includePdfLink"
                            text={t`FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_LINK`}
                        />
                    </Column>
                </Row>
            )}
        </>
    );
};

export const EncryptOption = () => {
    return (
        <Row>
            <Column>
                <Field.Checkbox
                    fieldClassName="field--no-height"
                    name="emailOptions.encrypt"
                    text={t`FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_ENCRYPT`}
                />
            </Column>
        </Row>
    );
};
